import React, { useMemo, useState, useEffect } from "react";
import {
  Row, Col, Card, Select, Button, Typography, Modal, ConfigProvider,
  notification,
  Spin, DatePicker,
  Form,
  Input,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import styled from "styled-components";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { getUserList, getUserInfo } from "redux/selectors";
import { bindActionCreators } from "redux";
import { connect, useSelector } from "react-redux";
import { generateNiceWeekFromWeek, generateTimesheetWeek } from "./useWeekData";
import en_GB from "antd/lib/locale-provider/en_GB";

const Wrapper = styled.div`
  padding: 16px;

  @media (min-width: 768px) {
    padding: 24px;
  }

  .month-select {
    width: 130px;

    @media (max-width: 576px) {
      width: 100%;
    }
  }

  .add-btn {
    background: #d9fff1;
    color: #0f766e;
    border: none;
    font-weight: 500;
  }

  .sub-header {
    margin: 16px 0;
  }

  .subtitle {
    font-weight: 500;
  }
`;

const StatCard = styled(Card)`
  background: #eef5ff;
  border-radius: 12px;
  text-align: center;
  border: none;

  .ant-card-body {
    padding: 16px;
  }

  .card-title {
    color: #6b7280;
    font-size: 13px;
  }

  .card-value {
    margin-top: 6px;
    color: #111827;
    font-size: 18px;
  }

  @media (max-width: 576px) {
    .ant-card-body {
      padding: 12px;
    }

    .card-value {
      font-size: 16px;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    border-bottom: none;
    padding-top: 10px;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-btn-text:focus,
  .ant-btn-text:hover {
    background: #26a4ff;
    color: #ffffff;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload-span {
    cursor: pointer;
  }
  .add-timesheet-box {
    padding: 20px;
    border: 1px solid #e8e8e8;
  }
`;

const { Title, Text } = Typography;


// const stats = [
//   { title: "Approved", value: 2 },
//   { title: "Rejected", value: 4 },
//   { title: "Pending - Approval", value: 32 },
//   { title: "Pending - Submission", value: 2 },
//   { title: "Total Hours", value: 4 },
//   { title: "Total Claims", value: 32 },
// ];

const AddTimesheet = ({
  form,
  handleClickTimeSheet,
  selectedWeek,
  setSelectedWeek,
  duplicateWeek,
  setDuplicateWeek,
}) => {
  const userInfo = useSelector(getUserInfo);



  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const weekStart = (week) => (week ? week.clone().startOf("isoWeek") : null);

  var weekEnd = (week) => (week ? week.clone().endOf("isoWeek") : null);

  const weekPeriod = selectedWeek
    ? generateTimesheetWeek(weekStart(selectedWeek), weekEnd(selectedWeek))
    : null;

  const period = selectedWeek ? generateNiceWeekFromWeek(weekPeriod) : "-";

  const { getTimesheet } = queryKeys;

  const { data: timesheetDataRes, isLoading: selectedTimesheetLoading } =
    useFetch(
      `${getTimesheet.key}-week:${weekPeriod}`,
      `${getTimesheet.url}?week=${weekPeriod}&employeeId=${userInfo.employer_id}`,
      {
        enabled: !!weekPeriod,
      }
    );

  const duplicateWeekPeriod = duplicateWeek
    ? generateTimesheetWeek(weekStart(duplicateWeek), weekEnd(duplicateWeek))
    : false;

  const {
    data: duplicateTimesheetDataRes,
    isLoading: isDuplicateTimesheetLoading,
  } = useFetch(
    `${getTimesheet.key}-week:${duplicateWeekPeriod}`,
    `${getTimesheet.url}?week=${duplicateWeekPeriod}&employeeId=${userInfo.employer_id}`,
    {
      enabled: !!duplicateWeekPeriod,
    }
  );

  const hasTimesheet = timesheetDataRes?.response?.response?.status || false;

  useEffect(() => {
    if (hasTimesheet) {
      setSelectedWeek(null);
      form.resetFields();
      notification.error({
        message: `Timesheet for week ${period} already exists`,
      });
    }
  }, [hasTimesheet, period, form]);

  const handleSubmit = () => {
    if (selectedWeek !== null && period !== "-") {
      const duplicateTimesheet =
        duplicateTimesheetDataRes?.response?.response?.timeSheet;

      if (duplicateTimesheet) {
        handleClickTimeSheet(
          period,
          duplicateTimesheet?.map((item) => {
            item.status = "pending";
            return item;
          })
        );
      } else {
        handleClickTimeSheet(period);
      }

      setSelectedWeek(null);
      setDuplicateWeek(null);
    }
  };

  return (
    <div className="add-timesheet-box">
      <Form
        form={form}
        layout="vertical"
        className="mt-2"
        onFinish={handleSubmit}
      >
        <Spin spinning={selectedTimesheetLoading}>
          <ConfigProvider locale={en_GB}>
            <Form.Item
              label="Select Week"
              name="week"
              rules={[{ required: true, message: "Please select week" }]}
            >
              <DatePicker
                picker="week"
                className="w-100"
                value={selectedWeek}
                onChange={(date) => {
                  setSelectedWeek(date);
                }}
              />
            </Form.Item>
          </ConfigProvider>
        </Spin>

        <Form.Item label="Period">
          <Input value={period} placeholder="Select Period" disabled={true} />
        </Form.Item>

        <ConfigProvider locale={en_GB}>
          <Form.Item label="Duplicate Week" name="duplicateWeek">
            <DatePicker
              picker="week"
              className="w-100"
              value={duplicateWeek}
              onChange={(date) => {
                setDuplicateWeek(date);
              }}
            />
          </Form.Item>
        </ConfigProvider>

        <Button
          htmlType="submit"
          type="primary"
          disabled={selectedTimesheetLoading || isDuplicateTimesheetLoading}
        >
          Add
        </Button>
      </Form>
    </div>
  );
};

const TimesheetData = (userInfo) => {
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [selectedTimesheetPeriod, setSelectedTimesheetPeriod] = useState(null);
  const [showAddTimeSheet, setShowAddTimeSheet] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [duplicateWeek, setDuplicateWeek] = useState(null);
  const [duplicateTimesheet, setDuplicateTimesheet] = useState(false);

  const [form] = Form.useForm();

  const handleClickTimeSheet = (period, duplicateSheet = false) => {
    setSelectedTimesheetPeriod(period);
    duplicateSheet && setDuplicateTimesheet(duplicateSheet);
    setShowTimesheet(true);
    setShowAddTimeSheet(false);
    form.resetFields();
  };

  const userId = userInfo?.userInfo?.userid;
  console.log(userId, "userInfodewde")


  const { key: timesheetKey, url: timesheetUrl } =
    queryKeys.getTimeSheetData(`?${`userid=${userId}`}`);

  const { data: timeSheetRes, refetch: timeSheetrefetch } = useFetch(
    timesheetKey,
    timesheetUrl
  );

  const timesheet = timeSheetRes?.response?.data;
  const timeSheetRows = timesheet?.timesheet?.timeSheet || [];

  console.log(timeSheetRows, "timeSheetRes")


  const stats = useMemo(() => {
    if (!timeSheetRows.length) return [];

    const pendingHours = timeSheetRows
      .filter((t) => t.status === "pending")
      .reduce((acc, t) => acc + (t.sum || 0), 0);

    const approvedHours = timeSheetRows
      .filter((t) => t.status === "approved")
      .reduce((acc, t) => acc + (t.sum || 0), 0);

    const rejectedHours = timeSheetRows
      .filter((t) => t.status === "rejected")
      .reduce((acc, t) => acc + (t.sum || 0), 0);

    const totalHours = timeSheetRows.reduce(
      (acc, t) => acc + (t.sum || 0),
      0
    );

    return [
      { title: "Approved", value: approvedHours },
      { title: "Rejected", value: rejectedHours },
      { title: "Pending - Approval", value: pendingHours },
      { title: "Pending - Submission", value: 0 }, // if not available
      { title: "Total Hours", value: totalHours },
      { title: "Total Claims", value: 0 }, // adjust if needed
    ];
  }, [timeSheetRows]);

  const cancelAddTimeSheet = () => {
    setShowAddTimeSheet(false);
  };

  const formatWeekRange = (weekStr) => {
    if (!weekStr) return "—";

    const [startRaw, endRaw] = weekStr.split("-");

    const parseDate = (str) => {
      const year = str.substring(0, 4);
      const month = str.substring(4, 7);
      const day = str.substring(7);

      return new Date(`${month} ${day}, ${year}`);
    };

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return n + "th";
      switch (n % 10) {
        case 1: return n + "st";
        case 2: return n + "nd";
        case 3: return n + "rd";
        default: return n + "th";
      }
    };

    const startDate = parseDate(startRaw);
    const endDate = parseDate(endRaw);

    const startDay = getOrdinal(startDate.getDate());
    const endDay = getOrdinal(endDate.getDate());

    const startMonth = startDate.toLocaleString("default", { month: "short" });
    const endMonth = endDate.toLocaleString("default", { month: "short" });
    const year = endDate.getFullYear();

    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
  };

  console.log(stats, "statsstats")
  return (
    <Wrapper>
      {/* Header */}
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} sm={12}>
          <Title level={4} style={{ margin: 0 }}>
            TimeSheet
          </Title>
        </Col>

        {/* <Col xs={24} sm={12}>
          <Row gutter={8} justify="end">
            <Col xs={12} sm="auto">
              <Button onClick={() => {
                setShowAddTimeSheet(true);
                setSelectedWeek(null);
                setDuplicateWeek(null);

              }} icon={<PlusOutlined />} className="add-btn" block>
                Add New
              </Button>
            </Col>
          </Row>
        </Col> */}
      </Row>

      {/* Sub Header */}
      <Row justify="space-between" className="sub-header">
        <Text className="subtitle">Recently Submitted</Text>
        <Text type="secondary">{formatWeekRange(timesheet?.week)}</Text>
      </Row>

      {/* Stats Cards */}
      <Row gutter={[12, 12]}>
        {stats.map((item, index) => (
          <Col xs={12} sm={12} md={8} key={index}>
            <StatCard>
              <Text className="card-title">{item.title}</Text>
              <div className="card-value">{item.value}</div>
            </StatCard>
          </Col>
        ))}
      </Row>
      <StyledModal
        open={showAddTimeSheet}
        onCancel={cancelAddTimeSheet}
        title={"Add Timesheet"}
        width={500}
        footer={null}
        closable={true}
        maskClosable={false}
        centered={true}
        destroyOnClose={true}
      >
        <AddTimesheet
          form={form}
          handleClickTimeSheet={handleClickTimeSheet}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          duplicateWeek={duplicateWeek}
          setDuplicateWeek={setDuplicateWeek}
        />
      </StyledModal>
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  userList: getUserList(state),
  userInfo: getUserInfo(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {

    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TimesheetData);
